import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Button from 'components/App/Button';
import INITIAL_VALUE from 'constants/initialValue';

import styles from './ModalEditAffiliatePolicy.module.scss';

export const AFFILIATE_FIELD = {
  INVITE_USER: 'inviteUser',
  INVITED_USER: 'invitedUser',
  INVITE_USER_SERVICE_PERCENT: 'inviteUserServicePercent',
};

const AFFILIATE_FIELDS = [
  {
    name: AFFILIATE_FIELD.INVITE_USER,
    label: 'Người mời',
    showCurrency: 'xu',
    decimalScale: 0,
  },
  {
    name: AFFILIATE_FIELD.INVITED_USER,
    label: 'Người được mời',
    showCurrency: 'xu',
    decimalScale: 0,
  },
  {
    name: AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT,
    label: 'Người mời thưởng thêm',
    showCurrency: '%',
    decimalScale: 2,
  },
];

const positiveNumberSchema = (message) => Yup.mixed()
  .required(message)
  .test({
    message: 'Vui lòng nhập số lớn hơn 0',
    test(value) {
      if (value === '' || value === null || value === undefined) return false;
      const number = Number(value);
      return !Number.isNaN(number) && number > 0;
    },
  });

const percentSchema = positiveNumberSchema('Phần trăm thưởng thêm không được để trống')
  .test({
    message: 'Phần trăm thưởng thêm không được lớn hơn 100%',
    test(value) {
      if (value === '' || value === null || value === undefined) return false;
      return Number(value) <= 100;
    },
  });

function ModalEditAffiliateMarketing(props) {
  const {
    isOpen,
    initialValues,
    isLoading,
    onToggleModal,
    onSubmit,
  } = props;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      [AFFILIATE_FIELD.INVITE_USER]: positiveNumberSchema('Xu người mời không được để trống'),
      [AFFILIATE_FIELD.INVITED_USER]: positiveNumberSchema('Xu người được mời không được để trống'),
      [AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]: percentSchema,
    }),
    onSubmit: (values) => {
      onSubmit({
        [AFFILIATE_FIELD.INVITE_USER]: Number(values[AFFILIATE_FIELD.INVITE_USER]),
        [AFFILIATE_FIELD.INVITED_USER]: Number(values[AFFILIATE_FIELD.INVITED_USER]),
        [AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT]: Number(
          values[AFFILIATE_FIELD.INVITE_USER_SERVICE_PERCENT],
        ),
      });
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    validation.resetForm({
      values: initialValues,
    });
  }, [isOpen, initialValues]);

  const CloseButton = useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  return (
    <Modal
      fade
      isOpen={isOpen}
      toggle={onToggleModal}
      className={styles.Modal}
    >
      <div className={styles.ModalHeaderWrapper}>
        <h2 className={styles.ModalHeaderTitle}>
          Chính sách tiếp thị liên kết
        </h2>

        <ModalHeader
          toggle={onToggleModal}
          close={CloseButton}
          className={styles.ModalHeader}
        />
      </div>

      <form onSubmit={validation.handleSubmit}>
        <ModalBody className={styles.ModalBody}>
          <div className={styles.FormList}>
            {AFFILIATE_FIELDS.map((field) => (
              <div key={field.name} className={styles.FormField}>
                <label className={styles.FormLabel} htmlFor={field.name}>
                  {field.label}
                </label>

                <AppInputNumberValidation
                  name={field.name}
                  id={field.name}
                  type="number"
                  placeholder="0"
                  validation={validation}
                  showCurrency={field.showCurrency}
                  value={validation.values[field.name]}
                  wrapperClassName={styles.InputWrapper}
                  inputClassName={styles.InputNumber}
                  thousandSeparator=""
                  decimalSeparator="."
                  formatOptions={{ decimalScale: field.decimalScale }}
                />
              </div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter className={styles.ModalFooter}>
          <div className={styles.FooterButton}>
            <Button
              classes="Button Button__Cancel"
              onClick={onToggleModal}
              disabled={isLoading}
            >
              Huỷ
            </Button>

            <Button
              classes="Button Button__Save"
              type="submit"
              disabled={isLoading}
            >
              Lưu
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}

ModalEditAffiliateMarketing.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialValues: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool,
  onToggleModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};

ModalEditAffiliateMarketing.defaultProps = {
  initialValues: INITIAL_VALUE.OBJECT,
  isLoading: false,
  onSubmit: INITIAL_VALUE.FUNCTION,
};

export default React.memo(ModalEditAffiliateMarketing);
